/**
 * The ruled data table — the spine of the entity-list and matrix-board archetypes.
 *
 * Built from a real `<table>` with `<th scope>`, deliberately. The landing mockups
 * render their tables as divs inside a `role="img"` with an `aria-hidden` subtree,
 * which is correct for a decorative marketing surface and would ship an inaccessible
 * app if copied. The design guide says exactly this: copy every visual decision, none
 * of the markup semantics.
 *
 * Three behaviours the guide asks for and that a plain table does not give you:
 *
 *  - a sticky header, so a hundred rows of matrix stay readable
 *  - the identity column pinned to the inline-start (right, in RTL) edge while the
 *    authority columns scroll
 *  - a footnote row that explains any non-obvious notation, which is what lets a
 *    dense ledger teach itself
 */
import type { ReactNode } from "react";
import { Skeleton } from "./primitives.js";

export interface Column<Row> {
  key: string;
  header: ReactNode;
  /** Rendered per row. Keep it presentational; put logic in the caller. */
  cell: (row: Row) => ReactNode;
  align?: "start" | "center" | "end";
  /** Pins this column to the inline-start edge. Use once, for identity. */
  sticky?: boolean;
  width?: string;
  /** Hidden below `lg`, for columns the mobile composition drops. */
  hideOnSmall?: boolean;
}

export interface DataTableProps<Row> {
  columns: Column<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  onRowClick?: ((row: Row) => void) | undefined;
  selectedKey?: string | null | undefined;
  /** The inline legend for any notation the table introduces. */
  footnote?: ReactNode | undefined;
  loading?: boolean | undefined;
  empty?: ReactNode | undefined;
  /** Compact trades padding for rows on screen; the matrix rewards it. */
  density?: "comfortable" | "compact";
  caption?: string;
  className?: string;
}

const ALIGN = { start: "text-start", center: "text-center", end: "text-end" } as const;

export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  onRowClick,
  selectedKey,
  footnote,
  loading = false,
  empty,
  density = "comfortable",
  caption,
  className = "",
}: DataTableProps<Row>) {
  const cellPadding = density === "compact" ? "px-3 py-1.5" : "px-3.5 py-2.5";

  if (loading) {
    return (
      <div className={`ruled overflow-hidden ${className}`} aria-busy="true">
        {Array.from({ length: 8 }, (_, rowIndex) => (
          <div
            key={rowIndex}
            className="flex items-center gap-4 border-b border-line-soft px-4 py-2.5 last:border-0"
          >
            <Skeleton className="h-3.5 w-44" />
            {columns.slice(1).map((column) => (
              <Skeleton key={column.key} className="h-3.5 flex-1" />
            ))}
          </div>
        ))}
      </div>
    );
  }

  if (rows.length === 0 && empty) {
    return <div className={`ruled ${className}`}>{empty}</div>;
  }

  return (
    <div className={`ruled overflow-hidden ${className}`}>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          {caption && <caption className="sr-only">{caption}</caption>}
          <thead>
            <tr className="border-b border-rule bg-paper">
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  style={column.width ? { width: column.width } : undefined}
                  className={[
                    // shadow-card on a sticky header is the one place a shadow
                    // earns its keep in a table: it tells you rows have scrolled
                    // underneath, which a hairline alone cannot.
                    "sticky top-0 z-20 bg-paper text-2xs font-semibold whitespace-nowrap text-ink-muted shadow-card",
                    cellPadding,
                    ALIGN[column.align ?? "start"],
                    // The identity column needs a higher stacking context than the
                    // other header cells, since it is sticky on both axes.
                    column.sticky ? "start-0 z-30 border-e border-rule" : "",
                    column.hideOnSmall ? "hidden lg:table-cell" : "",
                  ].join(" ")}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const key = rowKey(row);
              const selected = selectedKey === key;

              return (
                <tr
                  key={key}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  // A clickable row still needs to be reachable and operable by
                  // keyboard; the row itself carries the affordance.
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={
                    onRowClick
                      ? (event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            onRowClick(row);
                          }
                        }
                      : undefined
                  }
                  className={[
                    "border-b border-line-soft last:border-0",
                    selected ? "bg-primary-soft/50" : "hover:bg-wash/60",
                    onRowClick
                      ? "cursor-pointer focus-visible:outline focus-visible:-outline-offset-2 focus-visible:outline-2 focus-visible:outline-ledger"
                      : "",
                  ].join(" ")}
                >
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={[
                        cellPadding,
                        ALIGN[column.align ?? "start"],
                        column.sticky
                          ? `sticky start-0 z-10 border-e border-rule ${selected ? "bg-primary-soft/50" : "bg-surface"}`
                          : "",
                        column.hideOnSmall ? "hidden lg:table-cell" : "",
                      ].join(" ")}
                    >
                      {column.cell(row)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {footnote && (
        <p className="border-t border-line-soft bg-paper px-3.5 py-2 text-2xs text-ink-faint">
          {footnote}
        </p>
      )}
    </div>
  );
}

/**
 * The identity cell: initial avatar plus name, and an optional second line.
 *
 * Extracted because every list in the product opens with one, and the avatar's tone
 * has to stay consistent — it is the only place a coloured circle is allowed.
 */
export function IdentityCell({
  name,
  detail,
  href,
}: {
  name: string;
  detail?: ReactNode;
  href?: string;
}) {
  const initial = name.trim().charAt(0) || "?";

  return (
    <span className="flex items-center gap-2.5">
      <span
        className="grid size-6 shrink-0 place-items-center rounded-full bg-primary-soft text-2xs font-bold text-ledger"
        aria-hidden="true"
      >
        {initial}
      </span>
      <span className="min-w-0">
        {href ? (
          <a href={href} className="link-underline block truncate font-semibold text-ink">
            {name}
          </a>
        ) : (
          <span className="block truncate font-semibold text-ink">{name}</span>
        )}
        {detail && <span className="block truncate text-2xs text-ink-faint">{detail}</span>}
      </span>
    </span>
  );
}

/**
 * The relevance dot from the hero mockup's reporting board.
 *
 * A filled dot means the authority is relevant to this client this month; a hollow
 * ring means it is not. The distinction carries meaning, so it also carries a text
 * alternative — and the table that uses it carries the footnote explaining it.
 */
export function RelevanceDot({
  relevant,
  tone = "ledger",
  label,
}: {
  relevant: boolean;
  tone?: "ledger" | "ochre" | "red" | "pending";
  label: string;
}) {
  // The matrix draws one dot per authority, so these read as categories rather
  // than as statuses — hence the data ramp for the neutral cases. Red and ochre
  // keep their meanings: loss and lateness are never just a category.
  const fill = {
    ledger: "bg-data-1",
    ochre: "bg-ochre",
    red: "bg-red-ink",
    pending: "bg-data-3",
  }[tone];

  return (
    <span className="inline-flex items-center justify-center" title={label}>
      <span className="sr-only">{label}</span>
      <span
        className={
          relevant
            ? `size-2 rounded-full ${fill}`
            : "size-2 rounded-full border border-rule-strong bg-transparent"
        }
        aria-hidden="true"
      />
    </span>
  );
}
